import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Footer from '../Pages/Footer';
import toast, { Toaster } from 'react-hot-toast';
import { useUser } from '../context/UserContext';
import { API_URL } from '../config/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useUser();
  const navigate = useNavigate();

  // 🔹 Connexion
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Identifiants invalides');
        setLoading(false);
        return;
      }
      login(data);
      setEmail('');
      setPassword('');
      setLoading(false);
      toast.success('Connexion réussie 👋', { duration: 4000 });
      navigate('/');
      setTimeout(() => window.location.reload(), 1000);
    } catch {
      setError('Erreur serveur');
      setLoading(false);
    }
  };

  // 🔹 Demande réinitialisation mot de passe
  const handleResetPassword = async () => {
    if (!resetEmail) return toast.error('Veuillez saisir votre email');
    setResetLoading(true);
    try {
      const res = await fetch(`${API_URL}/user/reset-password-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || 'Erreur lors de l’envoi du lien');
      } else {
        toast.success('Lien envoyé ! Vérifiez votre boîte mail.');
        setModalOpen(false);
        setResetEmail('');
      }
    } catch {
      toast.error('Erreur serveur');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <div className="login-page">
        <Toaster position="top-right" reverseOrder={false} />

        <div className="login-container">
          <div className="login-card">
            <h2 className="login-header">Se connecter</h2>
            {error && <p className="login-error">{error}</p>}

            <form onSubmit={handleSubmit} className="login-form">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
              <input
                type="password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <button type="submit" className="btn-login" disabled={loading}>
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </form>

            <div className="login-footer">
              <p className="forgot-password" onClick={() => setModalOpen(true)}>
                Mot de passe oublié ?
              </p>
              <p>
                Pas encore de compte ?{' '}
                <Link to="/register" className="link-register">
                  S'inscrire
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* 🔹 Modal réinitialisation */}
        {modalOpen && (
          <div className="modal-backdrop" onClick={() => setModalOpen(false)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <h3>Réinitialiser le mot de passe</h3>
              <p>Entrez votre email pour recevoir un lien de réinitialisation.</p>
              <input
                type="email"
                placeholder="Votre email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={resetLoading}
              />
              <button onClick={handleResetPassword} disabled={resetLoading}>
                {resetLoading ? 'Envoi...' : 'Envoyer le lien'}
              </button>
              <button className="btn-close" onClick={() => setModalOpen(false)}>✕</button>
            </div>
          </div>
        )}

        {/* CSS intégré */}
        <style jsx>{`
          .login-page { min-height: 70vh; display: flex; justify-content: center; padding: 2rem; }
          .login-container { width: 100%; max-width: 400px; }
          .login-card { background: #fff; padding: 2rem; border-radius: 1rem; box-shadow: 0 10px 25px rgba(0,0,0,0.1); display: flex; flex-direction: column; gap: 1rem; }
          .login-header { text-align: center; font-size: 1.8rem; font-weight: 700; color: #333; margin-bottom: 1rem; }
          .login-error { color: #ff4d4f; text-align: center; font-weight: 500; }
          .login-form input { width: 100%; padding: 0.75rem 1rem; border: 1px solid #ddd; border-radius: 0.75rem; font-size: 1rem; margin-bottom: 1rem; outline: none; transition: all 0.2s ease; }
          .login-form input:focus { border-color: #667eea; box-shadow: 0 0 0 2px rgba(102,126,234,0.2); }
          .btn-login { width: 100%; padding: 0.75rem; background: #667eea; color: #fff; font-weight: 600; border: none; border-radius: 0.75rem; cursor: pointer; transition: all 0.2s ease; }
          .btn-login:hover { background: #5563d6; }
          .login-footer { text-align: center; margin-top: 1rem; }
          .forgot-password { color: #667eea; cursor: pointer; font-size: 0.9rem; text-decoration: underline; margin-bottom: 0.5rem; }
          .link-register { color: #667eea; font-weight: 600; text-decoration: none; }
          .link-register:hover { text-decoration: underline; }

          /* Modal */
          .modal-backdrop { position: fixed; inset:0; background: rgba(0,0,0,0.5); display:flex; justify-content:center; align-items:center; z-index:1000; }
          .modal-card { background:#fff; padding:2rem; border-radius:1rem; width: 90%; max-width:400px; position: relative; display:flex; flex-direction: column; gap:1rem; box-shadow:0 10px 25px rgba(0,0,0,0.2); }
          .modal-card h3 { margin:0; font-size:1.5rem; color:#333; }
          .modal-card input { padding:0.75rem 1rem; border:1px solid #ddd; border-radius:0.75rem; outline:none; transition: all 0.2s ease; }
          .modal-card input:focus { border-color:#667eea; box-shadow:0 0 0 2px rgba(102,126,234,0.2); }
          .modal-card button { padding:0.75rem; border:none; border-radius:0.75rem; font-weight:600; cursor:pointer; transition: all 0.2s ease; }
          .modal-card button:first-of-type { background:#667eea; color:#fff; margin-top:0.5rem; }
          .modal-card button:first-of-type:hover { background:#5563d6; }
          .btn-close { position:absolute; top:1rem; right:1rem; background:transparent; color:#999; font-size:1.2rem; cursor:pointer; }
        `}</style>
      </div>
      <Footer />
    </>
  );
};

export default Login;